// Platform Admin — Phase 3 WhatsApp ops panels (ADMIN_UPGRADE_PLAN 29–34).
// HealthPanel:     connection + Meta quality/tier per tenant, worst first.
// TemplatesPanel:  cross-tenant template queue with force re-sync.
// BroadcastsPanel: running/scheduled sends with pause / resume / cancel.
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  healthBoard, listTemplates, resyncTemplate, listBroadcasts, controlBroadcast,
  type HealthRow, type HealthSummary, type AdminTemplate, type AdminBroadcast,
} from '../../lib/padminOps';

const CARD = 'rounded-card border border-slate-200 bg-white p-5 shadow-sm';
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary disabled:opacity-40';
const TH = 'whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3';

function ago(ms: number | null): string {
  if (!ms) return '—';
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Err({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{msg}</div>;
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'good' | 'bad' }) {
  return (
    <div className={CARD}>
      <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-3">{label}</span>
      <b className={cn('mt-1 block text-2xl font-extrabold',
        tone === 'good' ? 'text-primary' : tone === 'bad' ? 'text-red-600' : 'text-ink')}>{value}</b>
    </div>
  );
}

// ── 29 + 32. Connection health ───────────────────────────────────────────────
const QUALITY_PILL: Record<string, string> = {
  GREEN: 'bg-green-100 text-green-700',
  YELLOW: 'bg-amber-100 text-amber-700',
  RED: 'bg-red-100 text-red-700',
};

export function HealthPanel({ onOpenTenant }: { onOpenTenant?: (id: string) => void }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<HealthRow[] | null>(null);
  const [sum, setSum] = useState<HealthSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null); setErr(null);
    healthBoard().then((r) => { setRows(r.rows); setSum(r.summary); }).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-ink-3">{t('pa.hl.note')}</p>
        <button type="button" onClick={load} className={cn(GHOST, 'ms-auto')}>{t('pa.hl.refresh')}</button>
      </div>
      <Err msg={err} />

      {sum && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Stat label={t('pa.hl.tenants')} value={sum.total} />
          <Stat label={t('pa.hl.connected')} value={sum.connected} />
          <Stat label={t('pa.hl.numbers')} value={sum.numbers ?? sum.connected} />
          <Stat label={t('pa.hl.live')} value={sum.live} tone="good" />
          <Stat label={t('pa.hl.problems')} value={sum.problems} tone={sum.problems > 0 ? 'bad' : undefined} />
        </div>
      )}

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse">
            <thead>
              <tr>
                {['pa.inv.col.tenant', 'pa.hl.col.number', 'pa.hl.col.state', 'pa.hl.col.quality', 'pa.hl.col.tier', 'pa.hl.col.issue'].map((k) => (
                  <th key={k} className={TH}>{t(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>}
              {rows?.map((r) => (
                <tr key={`${r.tenantId}:${r.integrationId ?? 'none'}`} className={cn('border-t border-slate-200', r.severity >= 40 && 'bg-red-50/50')}>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    <button type="button" onClick={() => onOpenTenant?.(r.tenantId)}
                      className="text-[13px] font-semibold text-ink hover:text-primary hover:underline">
                      {r.tenantName}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 font-mono text-[12px] text-ink-2">
                    {r.verifiedPhoneE164 ?? '—'}
                    {r.label && <span className="ms-1.5 font-sans text-[11px] text-ink-3">{r.label}</span>}
                    {r.coexistence && <span className="ms-1" title={t('accounts.coexBadge')}>📱</span>}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {!r.connected && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-ink-3">{t('pa.hl.notConnected')}</span>}
                      {r.connected && r.isLive && !r.isPaused && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-bold text-green-700">{t('pa.hl.stLive')}</span>}
                      {r.connected && !r.isLive && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-700">{t('pa.hl.stSandbox')}</span>}
                      {r.isPaused && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">{t('pa.hl.stPaused')}</span>}
                      {r.connected && !r.hasToken && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">{t('pa.hl.noToken')}</span>}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    {r.qualityRating ? (
                      <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', QUALITY_PILL[r.qualityRating] ?? 'bg-slate-100 text-ink-2')}>
                        {r.qualityRating}
                      </span>
                    ) : <span className="text-xs text-ink-3">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[12px] text-ink-2">{r.messagingLimit ?? '—'}</td>
                  <td className="max-w-[240px] px-3.5 py-3 text-[11px] text-red-600">{r.metaError ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 30. Template queue ───────────────────────────────────────────────────────
const TPL_PILL: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-700',
  PENDING: 'bg-amber-100 text-amber-700',
  REJECTED: 'bg-red-100 text-red-700',
};

export function TemplatesPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState<AdminTemplate[] | null>(null);
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    listTemplates(filter || undefined, 200).then((r) => setRows(r.templates)).catch((e: Error) => setErr(e.message));
  }, [filter]);
  useEffect(load, [load]);

  async function resync(row: AdminTemplate) {
    setBusy(row.docId); setErr(null);
    try {
      const r = await resyncTemplate(row.tenantId, row.docId);
      if (!r.synced) setErr(t(r.reason === 'no_live_token' ? 'pa.tpl.noToken' : 'pa.tpl.notAtMeta'));
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  const TABS = [
    { k: '', label: t('pa.inv.filter.all') },
    { k: 'PENDING', label: t('pa.tpl.pending') },
    { k: 'APPROVED', label: t('pa.tpl.approved') },
    { k: 'REJECTED', label: t('pa.tpl.rejected') },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-ink-3">{t('pa.tpl.note')}</p>
        <div className="ms-auto flex gap-1">
          {TABS.map((tab) => (
            <button key={tab.k || 'all'} type="button" onClick={() => setFilter(tab.k)}
              className={cn('rounded-full px-3 py-1 text-xs font-bold',
                filter === tab.k ? 'bg-ink text-white' : 'border border-slate-200 bg-white text-ink-3 hover:text-ink')}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <Err msg={err} />

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr>
                {['pa.inv.col.tenant', 'pa.tpl.col.name', 'pa.tpl.col.lang', 'pa.tpl.col.category', 'pa.inv.col.status', 'pa.tpl.col.synced', 'pa.t.col.actions'].map((k) => (
                  <th key={k} className={TH}>{t(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={7} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>}
              {rows?.length === 0 && <tr><td colSpan={7} className="px-3.5 py-8 text-center text-sm text-ink-3">{t('pa.tpl.empty')}</td></tr>}
              {rows?.map((r) => (
                <tr key={`${r.tenantId}/${r.docId}`} className="border-t border-slate-200">
                  <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-semibold text-ink">{r.tenantName}</td>
                  <td className="px-3.5 py-3 font-mono text-[12px] text-ink-2">{r.name}</td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[12px] text-ink-2">{r.language}</td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-[12px] text-ink-2">{r.category}</td>
                  <td className="px-3.5 py-3">
                    <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', TPL_PILL[r.status] ?? 'bg-slate-100 text-ink-2')}>
                      {r.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-3 text-xs text-ink-3">{ago(r.lastSyncedAt)}</td>
                  <td className="whitespace-nowrap px-3.5 py-3">
                    <button type="button" onClick={() => void resync(r)} disabled={busy === r.docId} className={GHOST}>
                      {busy === r.docId ? '…' : t('pa.tpl.resync')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 31. Broadcast oversight ──────────────────────────────────────────────────
const BC_PILL: Record<string, string> = {
  sending: 'bg-blue-100 text-blue-700',
  scheduled: 'bg-amber-100 text-amber-700',
  paused: 'bg-slate-200 text-ink',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  draft: 'bg-slate-100 text-ink-3',
};

export function BroadcastsPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState<AdminBroadcast[] | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    listBroadcasts(activeOnly || undefined, 150).then((r) => setRows(r.broadcasts)).catch((e: Error) => setErr(e.message));
  }, [activeOnly]);
  useEffect(load, [load]);

  async function control(row: AdminBroadcast, action: 'pause' | 'resume' | 'cancel') {
    const reason = window.prompt(t(action === 'cancel' ? 'pa.bc.cancelPrompt' : 'pa.bc.reasonPrompt'));
    if (!reason || reason.trim().length < 3) return;
    setBusy(row.id); setErr(null);
    try {
      await controlBroadcast({ tenantId: row.tenantId, broadcastId: row.id, action, reason: reason.trim() });
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-ink-3">{t('pa.bc.note')}</p>
        <label className="ms-auto flex items-center gap-2 text-xs font-bold text-ink-2">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          {t('pa.bc.activeOnly')}
        </label>
        <button type="button" onClick={load} className={GHOST}>{t('pa.hl.refresh')}</button>
      </div>
      <Err msg={err} />

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse">
            <thead>
              <tr>
                {['pa.inv.col.tenant', 'pa.bc.col.name', 'pa.inv.col.status', 'pa.bc.col.progress', 'pa.bc.col.failed', 'pa.t.col.actions'].map((k) => (
                  <th key={k} className={TH}>{t(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">…</td></tr>}
              {rows?.length === 0 && <tr><td colSpan={6} className="px-3.5 py-8 text-center text-sm text-ink-3">{t('pa.bc.empty')}</td></tr>}
              {rows?.map((r) => {
                const live = r.status === 'sending' || r.status === 'scheduled';
                return (
                  <tr key={`${r.tenantId}/${r.id}`} className="border-t border-slate-200">
                    <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-semibold text-ink">{r.tenantName}</td>
                    <td className="px-3.5 py-3 text-[13px] text-ink-2">
                      {r.name}
                      {r.templateName && <span className="ms-2 font-mono text-[11px] text-ink-3">{r.templateName}</span>}
                    </td>
                    <td className="px-3.5 py-3">
                      <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', BC_PILL[r.status] ?? 'bg-slate-100 text-ink-2')}>
                        {r.status}
                      </span>
                      {r.pausedByAdmin && <span className="ms-1 text-[10px] font-bold text-ink-3">({t('pa.bc.byAdmin')})</span>}
                    </td>
                    <td className="whitespace-nowrap px-3.5 py-3 text-[12px] text-ink-2">
                      {r.sent.toLocaleString()}{r.audience > 0 ? ` / ${r.audience.toLocaleString()}` : ''}
                    </td>
                    <td className={cn('whitespace-nowrap px-3.5 py-3 text-[12px] font-bold', r.failed > 0 ? 'text-red-600' : 'text-ink-3')}>
                      {r.failed.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3.5 py-3">
                      <div className="flex gap-1.5">
                        {live && (
                          <button type="button" disabled={busy === r.id} className={GHOST}
                            onClick={() => void control(r, 'pause')}>{t('pa.bc.pause')}</button>
                        )}
                        {r.status === 'paused' && (
                          <button type="button" disabled={busy === r.id} className={GHOST}
                            onClick={() => void control(r, 'resume')}>{t('pa.bc.resume')}</button>
                        )}
                        {(live || r.status === 'paused') && (
                          <button type="button" disabled={busy === r.id}
                            className={cn(GHOST, 'hover:border-red-300 hover:text-red-600')}
                            onClick={() => void control(r, 'cancel')}>{t('pa.bc.cancel')}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
